/**
 * mcpbClips.js — pure clip-time math for MCPlayback playback + timeline.
 *
 * A "clip" is a frozen event from event.getEventsByTime (see rda-backend event._freezeEvent):
 *   { bID, bCamera, sTimestamp:'YYYYMMDDHHMMSS' (clip START), sMovie (file), bLength (duration s),
 *     sPreview, fArchive, fExport, fCloudVS, fSmartFill, sTags, ... }
 *
 * The playback "window" is the committed search span [selectedTimestamp, +windowSeconds]. The shared
 * playhead is expressed as OFFSET SECONDS from the window start (0 .. windowLen) — clean for the
 * timeline's left/width % math (1f) and the transport scrub. These helpers map between the playhead
 * offset and the clip that covers it. All math is whole-second + DST-immune via mcpbTime.secondsBetween.
 *
 * Scope note (1e): clip-boundary detection here uses bLength for the clip span; the live <video>'s
 * "ended"/decoded duration refines the actual boundary at play time. Sub-second drift correction +
 * buffer-gate are the 2a multi-cam sync concern (see feature-inventory §12), not here.
 */
import { secondsBetween } from './mcpbTime';

/** Clip duration in whole seconds (>= 0). bLength is the DB-recorded length; missing/NaN -> 0. */
export const clipDurationSec = (ev) => {
  const n = Number(ev && ev.bLength);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
};

/** Offset (seconds from window start) of a clip's START. May be negative if the clip predates the window. */
export const clipStartOffset = (ev, windowStartTs) =>
  secondsBetween(windowStartTs, ev && ev.sTimestamp);

/** Offset (seconds from window start) of a clip's END = start + duration. */
export const clipEndOffset = (ev, windowStartTs) =>
  clipStartOffset(ev, windowStartTs) + clipDurationSec(ev);

/**
 * Return a NEW array of clips sorted by start time ascending (malformed-timestamp clips drop to the
 * end). Never mutates the input. Callers that scan for "the clip at T" / "the next clip" rely on order.
 */
export const sortClips = (clips) =>
  (clips || []).slice().sort((a, b) => {
    const oa = clipStartOffset(a, '19700101000000');
    const ob = clipStartOffset(b, '19700101000000');
    if (Number.isNaN(oa)) return 1;
    if (Number.isNaN(ob)) return -1;
    return oa - ob;
  });

/**
 * The clip covering `offsetSec` (seconds from window start), with the offset INTO that clip.
 * Half-open interval [start, start+duration) so back-to-back clips never both match a boundary second.
 *
 * @returns {{clip: object, offsetInClip: number}|null}  null when `offsetSec` lands in a gap (no clip).
 */
export const clipAtOffset = (clips, offsetSec, windowStartTs) => {
  for (const ev of sortClips(clips)) {
    const start = clipStartOffset(ev, windowStartTs);
    if (Number.isNaN(start)) continue;
    const end = start + clipDurationSec(ev);
    if (offsetSec >= start && offsetSec < end) {
      return { clip: ev, offsetInClip: offsetSec - start };
    }
  }
  return null;
};

/**
 * Offset of the NEXT clip's start strictly after `offsetSec`, or null if none remain. Used by the
 * Forward transport button and auto-skip-gaps (jump forward over dead air to the next recorded clip).
 */
export const nextClipStartOffset = (clips, offsetSec, windowStartTs) => {
  let best = null;
  for (const ev of clips || []) {
    const start = clipStartOffset(ev, windowStartTs);
    if (Number.isNaN(start) || start <= offsetSec) continue;
    if (best === null || start < best) best = start;
  }
  return best;
};

/**
 * The NEXT clip object strictly after `offsetSec`, returned ONLY once the playhead is within
 * `leadSec` of its start — the port of Java MCTimelineView.getNextUnplayedClip (the prefetch/warming
 * target). Warming this clip during the current one's playback is what makes the boundary seamless
 * (no per-clip first-frame spinner); the lead-time gate keeps us from warming every future clip at
 * once (Java BASE_PRELOAD_SECONDS window). Builds on nextClipStartOffset (earliest start > offset) +
 * clipAtOffset. Returns null when no clip remains ahead, or the next one is still farther than
 * `leadSec` away.
 *
 * @param {Array}  clips         this track's clips (frozen events)
 * @param {number} offsetSec     current playhead offset (seconds from window start)
 * @param {string} windowStartTs committed window start 'YYYYMMDDHHMMSS'
 * @param {number} [leadSec]     only return the clip within this many seconds of its start (default: no gate)
 * @returns {object|null} the next clip event, or null
 */
export const nextUnplayedClip = (clips, offsetSec, windowStartTs, leadSec = Infinity) => {
  const startOff = nextClipStartOffset(clips, offsetSec, windowStartTs);
  if (startOff === null) return null;
  if (startOff - offsetSec > leadSec) return null; // too far ahead — don't warm yet
  const found = clipAtOffset(clips, startOff, windowStartTs);
  return found ? found.clip : null;
};

/**
 * Offset of the PREVIOUS clip's start strictly before `offsetSec`, or null if none. Powers the Back
 * transport button: pressing Back while mid-clip lands on that clip's own start (restart it); pressing
 * Back again (now at the clip start) steps to the prior clip — the familiar track-skip feel.
 */
export const prevClipStartOffset = (clips, offsetSec, windowStartTs) => {
  let best = null;
  for (const ev of clips || []) {
    const start = clipStartOffset(ev, windowStartTs);
    if (Number.isNaN(start) || start >= offsetSec) continue;
    if (best === null || start > best) best = start;
  }
  return best;
};

/** Offset of the EARLIEST clip start (may be negative for a clip predating the window), or null. */
export const firstClipStartOffset = (clips, windowStartTs) => {
  let best = null;
  for (const ev of clips || []) {
    const start = clipStartOffset(ev, windowStartTs);
    if (Number.isNaN(start)) continue;
    if (best === null || start < best) best = start;
  }
  return best;
};

/**
 * First offset WITHIN the window that has playable content — where to land the playhead so a frame
 * shows immediately (not a leading gap). 0 if a clip covers the window start (e.g. a clip that began
 * before the window and is still running); otherwise the first clip start after 0; else 0 (no clips).
 *
 * The window start can fall in a gap even when earlier clips exist: a clip that ends exactly at the
 * window start (lookback math) contributes nothing in-window, so we skip to the next clip's start.
 */
export const firstPlayableOffset = (clips, windowStartTs) => {
  if (clipAtOffset(clips, 0, windowStartTs)) return 0;
  const next = nextClipStartOffset(clips, 0, windowStartTs);
  return next !== null && next > 0 ? next : 0;
};

/**
 * Flatten every filled slot's clips into ONE array — the basis for the master clock's prev/next-clip
 * + auto-skip decisions across ALL tracks (Java MCTimelineView.getNextClipStartAcrossAllTracks).
 * `clipAtOffset(union)` then answers "does ANY track have video at this offset" (so auto-skip only
 * fires when every track is dark), and `next/prevClipStartOffset(union)` give the earliest/latest
 * clip boundary across tracks. Order is irrelevant (callers scan for min/max). Null/empty-safe.
 *
 * Each SlotCell still resolves its OWN track via clipAtOffset(slotClips) for per-cell gap rendering;
 * this is purely the shared-clock view of "where is there video anywhere".
 *
 * @param {Array}  slots        the conf slots ({bSerial, bCamera} | null)
 * @param {object} trackEvents  map keyed `${serial}_${bCamera}` → that track's clips
 */
export const unionTrackClips = (slots, trackEvents) => {
  const out = [];
  (slots || []).forEach((cam) => {
    if (!cam) return;
    const clips = trackEvents && trackEvents[`${cam.bSerial}_${cam.bCamera}`];
    if (clips && clips.length) out.push(...clips);
  });
  return out;
};
